import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Images, ArrowRight } from 'lucide-react';
import Seo from '@/components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryPage() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/gallery/albums`)
      .then(({ data }) => setAlbums(Array.isArray(data) ? data : []))
      .catch(() => setAlbums([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-28 pb-20 min-h-screen bg-[#FAF9F6]" data-testid="gallery-page">
      <Seo
        title="The Studio Gallery"
        description="A quiet collection of moments from the Whispering Wristwear studio in Paphos — bracelets in progress, markets we've stood at, and the small, slow things in between."
        path="/gallery"
      />

      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
        <header className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-caption text-[#84A98C] mb-3">The Studio Gallery</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-5 leading-tight">
            Quiet moments from the bench
          </h1>
          <div className="w-12 h-px bg-[#D4AF37] mx-auto mb-5" />
          <p className="text-[#52796F] leading-relaxed">
            A collection of glances into the studio — bracelets in progress,
            markets we&apos;ve stood at, parcels going out the door, and the
            small, slow things in between.
          </p>
          <Link
            to="/gallery/submit"
            className="inline-flex items-center gap-2 mt-6 text-[#354F52] hover:text-[#D4AF37] text-sm uppercase tracking-widest transition-colors"
            data-testid="gallery-submit-cta"
          >
            Share a photo wearing yours
            <span aria-hidden="true">→</span>
          </Link>
        </header>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="aspect-[4/5] bg-[#F5F5F0] rounded-sm animate-pulse"
              />
            ))}
          </div>
        ) : albums.length === 0 ? (
          <div className="text-center py-16 bg-[#F5F5F0] rounded-sm" data-testid="gallery-empty">
            <Images
              className="w-10 h-10 text-[#84A98C] mx-auto mb-4"
              strokeWidth={1.25}
            />
            <p className="font-serif text-xl text-[#354F52] mb-2">
              The gallery is being arranged
            </p>
            <p className="text-[#52796F] text-sm max-w-md mx-auto">
              Lindy is choosing which photographs to share first. Come back soon —
              this corner of the site is about to get warmer.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="gallery-albums-grid"
          >
            {albums.map((album) => (
              <AlbumCard key={album.id} album={album} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AlbumCard({ album }) {
  return (
    <Link
      to={`/gallery/${album.slug}`}
      className="group block"
      data-testid={`gallery-album-card-${album.slug}`}
    >
      <div className="relative aspect-[4/5] bg-[#F5F5F0] rounded-sm overflow-hidden">
        {album.cover_image ? (
          <img
            src={album.cover_image}
            alt={album.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Images
              className="w-10 h-10 text-[#84A98C]"
              strokeWidth={1.25}
            />
          </div>
        )}
        {/* Bottom gradient + count */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#354F52]/85 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
              {album.photo_count} photo{album.photo_count === 1 ? '' : 's'}
            </p>
            <ArrowRight
              className="w-4 h-4 text-[#FAF9F6] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all"
              strokeWidth={1.5}
            />
          </div>
          <h2 className="font-serif text-2xl text-[#FAF9F6] mt-2 leading-tight">
            {album.title}
          </h2>
          {album.description && (
            <p className="text-sm text-[#FAF9F6]/85 mt-1 line-clamp-2 leading-relaxed">
              {album.description}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}
